import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import MetsIcon from 'commons/components/MetsIcon';

import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import { UserRegistrationStatus } from 'UserRegistration/resources/utils/UserRegistrationStatus';
import { formatPrice } from '../../../commons/resources/utils/numeric';
import {isPromoCodeApplicable} from "../../resources/utils/packageSelectionUtils";

function AddContactConfirmationPage() {

    useEffect(() => {
        window.history.pushState(null, t('confirmation.windowTitle.addContact'), '/private/user-add-contact/confirmation');
        window.scrollTo(0, 0);
    }, []);  

    const { componentConfig } = useContext(ConfigurationContext);
    const { registrationData } = useContext(DataContext);
    const { t, i18n } = useTranslation(componentConfig.i18nNamespaces);

    const model = registrationData.registrationModel;
    const useNewCard = model.billingInformation.selectedPayWithNewCard; 
    const ccType = useNewCard ? registrationData.creditCardInfo.type : t(`mpg_${model.billingInformation.existingCCType}`);
    const cclastFourDigits = useNewCard ? registrationData.creditCardInfo.lastFourDigits : model.billingInformation.existingCCLastFourDigits;

    const renderSubscriptionPricingDetails = () => {
        let subscriptionPricingDetails = formatPrice(registrationData.priceTaxesInfo.totalBeforeTaxes, i18n.language, componentConfig.currencyCode) + ' ' + t('plusApplicableTaxes');

        if (registrationData.registrationModel.promotion.type == "FREE_MONTHS" && isPromoCodeApplicable(registrationData)) {
            subscriptionPricingDetails = formatPrice(0, i18n.language, componentConfig.currencyCode);
        }

        return subscriptionPricingDetails;
    }
    
    const renderSubscriptionOrderDetails = () => {

        // If we have multiple selected subscription items, we will display the total amount of items selected.
        if (componentConfig.componentDefinition.PublicPackageSelector.props.showSynchronizedSubscriptionItemSelector) {
            if (registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length > 1) {
                return t(model.subscription.selectedGroup.cmsKey + '.optionName') + ' - ' + t('confirmation.numberOfItems', { numberOfItems: registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length });
            }
        }

        // If we don't, we simply display the name of the single subscription item they selected.
        return t(model.subscription.selectedGroup.cmsKey + '.optionName') + ' - ' + registrationData.priceTaxesInfo.orderedPackage;

    };

    const renderSecondarySubscriptionItemOrderDetails = () => {

        const secondarySubscriptionTitles = model.selectedSecondarySubscriptions.map((subscriptionItem) => {
            return subscriptionItem.description;
        }).join(', ');
        
        if (secondarySubscriptionTitles.length) {
            return (
                <span>
                    {componentConfig.isPCEnabled && t('pc.confirmationTitle') + " - "}
                    {secondarySubscriptionTitles}
                </span>
            )
        }
    }
    const creditCardTypeIcon = () => {
        switch (ccType) {
            case t('svg_VISA_alt'):
                return <MetsIcon name="svg_VISA" className="colorVISA" key="VISA" />
            case t('svg_MC_alt'):
                return <MetsIcon name="svg_color_MC" className="colorMC" key="color_MC" />
            case t('svg_AE_alt'):
                return <MetsIcon name="svg_AE" className="colorAE" key="AE" />
            default:
                return <></>;
        }
    }

    return (
        <>
            <h1 className="registration-confirmation-header">
                <MetsIcon name="svg_circle_checkmark_thin" className="numbered-section-complete-icon" />
                {t('confirmation.title.1.addContact')} <br />
                {t('confirmation.title.2.addContact', {firstName: model.personalInformation.userFirstName,
                                        lastName: model.personalInformation.userLastName})}
            </h1>
            <div className="registration-confirmation-body">
                {registrationData.metadata.status !== UserRegistrationStatus.COMPLETED && <div className="warning-messages">
                    <MetsIcon name="svg_warning" />
                    <div className="warning">{t('confirmation.warning')}</div>
                </div>}
                <p className="registration-confirmation-desc">
                    {t('confirmation.emailActivation.1.addContact')}<strong>{model.personalInformation.emailAddress}</strong>{t('confirmation.emailActivation.2.addContact')}
                </p>
                <div className="confirmation-subscription-details">
                    <h2>{t('subscriptionDetails')}</h2>
                    <p className="subscription-list">
                        <span>{t('confirmation.subscriptionDetails.1')}</span>
                        <span className="confirmation-subscription-details-val">
                            <span className="confirmation-subscription-primary-details">{renderSubscriptionOrderDetails()}</span>
                            <span className="confirmation-subscription-secondary-details">{renderSecondarySubscriptionItemOrderDetails()}</span>
                        </span>
                    </p>
                    <p>{t('confirmation.subscriptionDetails.2')} <span className="confirmation-subscription-details-val">{renderSubscriptionPricingDetails()}</span></p>
                </div>
                <div className="confirmation-primary-contact">
                    <h2>{t('contact')}</h2>
                    <p>{model.personalInformation.userFirstName} {model.personalInformation.userLastName}</p>
                    <p>{model.personalInformation.organizationName}</p>
                    <p>{model.personalInformation.emailAddress}</p>
                    <p>{model.personalInformation.phoneNumber}</p>
                </div>
                {registrationData.metadata.isBillingRequired && <div className="confirmation-payment-method">
                    <h2>{t('billingInformation')}</h2>
                    <p>
                        {creditCardTypeIcon()}
                        {ccType} {cclastFourDigits ? t('endingWith') : ''} {cclastFourDigits}
                    </p>
                </div>}
            </div>

            <div className="confirmation-contactPageLink">
                <a href="/private/supplier/contacts">{t('contactPageLink')}</a> {'>'}
            </div>
        </>
    );

}

export default AddContactConfirmationPage;